import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { TARGETS, CONCEPTS } from "../utils/buildPrompt";

const MONO = "'DM Mono', monospace";
const INK = '#1a1a1a';
const FAINT = '#8a8a84';
const HL = '#e4e4de';

// 모눈종이 배경
const GRID_BG = {
  backgroundColor: '#f6f6f2',
  backgroundImage: 'linear-gradient(#e9e9e3 1px, transparent 1px), linear-gradient(90deg, #e9e9e3 1px, transparent 1px)',
  backgroundSize: '20px 20px',
};

// 미리보기용 샘플 데이터 (/share/demo)
const DEMO_DATA = {
  tab: 'target',
  target: 'client',
  level_idx: 0,
  concept: null,
  input_text: '오늘부로 폐업해서 진행하기로 햇던 플젝 취소됩니다 죄송요',
  result_text: '안녕하세요. 갑작스러운 소식을 전하게 되어 죄송합니다. 내부 사정으로 회사 운영이 어려워져, 진행 예정이던 프로젝트를 부득이하게 취소하게 되었습니다. 넓은 양해 부탁드립니다.',
};

function getModeLabel(item) {
  if (item.tab === 'target' && item.target) {
    const label = TARGETS[item.target]?.label ?? item.target;
    const level = TARGETS[item.target]?.levels?.[item.level_idx] ?? '';
    return `${label} · ${level}`.trim();
  }
  if (item.tab === 'concept' && item.concept) {
    return CONCEPTS[item.concept]?.label ?? item.concept;
  }
  return '-';
}

const hlChip = { background: HL, padding: '3px 10px', fontSize: 14, color: INK };

// 섹션 라벨: 작고 흐리게 (위계 하단)
const sectionLabel = { fontSize: 13, fontWeight: 700, color: FAINT, letterSpacing: 2, marginBottom: 12 };

function CenterMsg({ children }) {
  return (
    <div style={{ ...GRID_BG, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: MONO, fontSize: 15, color: FAINT }}>
      {children}
    </div>
  );
}

export default function SharePage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (id === 'demo') {
      setData(DEMO_DATA);
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        // 서버 API를 통해 조회 (권한 문제 없이 누구나 열람 가능)
        const res = await fetch(`/api/share?id=${encodeURIComponent(id)}`);
        if (!res.ok) setNotFound(true);
        else setData(await res.json());
      } catch {
        setNotFound(true);
      }
      setLoading(false);
    };
    load();
  }, [id]);

  const isReply = data?.mode === 'reply';

  if (loading) return <CenterMsg>불러오는 중...</CenterMsg>;
  if (notFound) return <CenterMsg>404 · 존재하지 않는 진단서예요</CenterMsg>;

  return (
    <div style={{ minHeight: '100vh', background: '#eaeae6', display: 'flex', justifyContent: 'center' }}>
      <div style={{ ...GRID_BG, width: '100%', maxWidth: 430, minHeight: '100vh', fontFamily: MONO, color: INK, display: 'flex', flexDirection: 'column' }}>

        {/* 상단 바 */}
        <div style={{ padding: '20px 20px 0', display: 'flex', alignItems: 'center' }}>
          <span style={{ ...hlChip, fontWeight: 700 }}>말투좀요</span>
          <span style={{ marginLeft: 'auto', fontSize: 13, color: FAINT }}>사회생활 진단서</span>
        </div>

        <div style={{ padding: '32px 20px 0', flex: 1 }}>

          {/* 문서 제목 — 위계 최상단 */}
          <div style={{ fontSize: 25, fontWeight: 900, fontFamily: "'Noto Sans KR', sans-serif", letterSpacing: -0.5, lineHeight: 1.4, borderBottom: `2px solid ${INK}`, paddingBottom: 18, marginBottom: 20 }}>
            {isReply ? <>키보드 워리어<br />답장 처방전</> : <>키보드 워리어<br />교정 결과지</>}
          </div>

          {/* 메타 정보 — 보조 */}
          <div style={{ fontSize: 14, color: FAINT, marginBottom: 44 }}>
            ✛ 변환 대상 <span style={{ ...hlChip, marginLeft: 8 }}>{getModeLabel(data)}</span>
          </div>

          {/* BEFORE */}
          <div style={{ marginBottom: 40 }}>
            <div style={sectionLabel}>{isReply ? '▼ 받은 메시지' : '▼ 수술 전'}</div>
            <div style={{ background: HL, padding: '18px 17px', fontSize: 17, lineHeight: 1.6, wordBreak: 'keep-all', color: '#55554f' }}>
              {data.input_text}
            </div>
            {!isReply && (
              <div style={{ fontSize: 14, marginTop: 12 }}>
                <span style={{ color: FAINT }}>⚠ 판정 → </span>
                <span style={{ background: INK, color: '#fff', padding: '2px 10px' }}>사회생활 불가</span>
              </div>
            )}
          </div>

          {/* 수술 진행 */}
          <div style={{ fontSize: 13, color: FAINT, textAlign: 'center', margin: '40px 0', letterSpacing: 1 }}>
            {isReply ? '답장 처방 [██████████] 100%' : '말투 수술 [██████████] 100%'}
          </div>

          {/* AFTER */}
          <div style={{ marginBottom: 44 }}>
            <div style={sectionLabel}>{isReply ? '▼ 모범 답장' : '▼ 수술 후'}</div>
            <div style={{ background: INK, color: '#f6f6f2', padding: '18px 17px', fontSize: 17, lineHeight: 1.6, wordBreak: 'keep-all' }}>
              {data.result_text}
            </div>
            <div style={{ fontSize: 14, marginTop: 12 }}>
              <span style={{ color: FAINT }}>✓ 판정 → </span>
              <span style={{ ...hlChip, fontWeight: 700 }}>{isReply ? '이대로 전송 가능' : '사회생활 가능'}</span>
            </div>
          </div>

          {/* 최종 소견 — 보조 */}
          <div style={{ borderTop: `1px dashed ${FAINT}`, paddingTop: 24, fontSize: 14, color: '#55554f', lineHeight: 2, marginBottom: 40 }}>
            {isReply ? (
              <>
                <div>→ 답장 고민 시간 0분으로 단축</div>
                <div>→ 현생 복귀 승인</div>
              </>
            ) : (
              <>
                <div>→ 키보드 워리어에서 사회인으로 전환 완료</div>
                <div>→ 현생 복귀 승인</div>
              </>
            )}
          </div>

        </div>

        {/* CTA */}
        <div style={{ padding: '0 20px 32px' }}>
          <button
            onClick={() => window.location.href = '/'}
            style={{ width: '100%', background: INK, color: '#fff', border: 'none', padding: '17px 0', fontSize: 17, fontWeight: 700, letterSpacing: 2, cursor: 'pointer', fontFamily: MONO }}>
            ▶ 나도 수술받기
          </button>
          <div style={{ fontSize: 13, color: FAINT, textAlign: 'center', marginTop: 12 }}>무료로 시작 · 지금 바로 변환</div>
        </div>

      </div>
    </div>
  );
}
